import { AvailablePermissions } from 'src/components/app/types';
import { ChannelAccessLevel, SolveChannel } from 'src/services/apiInterfaces';
import { CurrentUserChannelAccess } from 'src/services/dashboard-api/types';
import { SolveWidgetProduct } from 'src/types/types';
import { Permission } from 'src/utils/enums';

const LEVEL_RANK: Record<ChannelAccessLevel, number> = {
  edit: 2,
  none: 0,
  publish: 3,
  view: 1,
};

const PRODUCT_TO_CHANNEL: Partial<Record<SolveWidgetProduct, SolveChannel>> = {
  api: 'headless',
  interactive_email: 'email',
  slack: 'slack',
  voice: 'voice',
  web_call: 'voice',
  workflow_builder: 'widget',
};

export const ACTION_PERMISSIONS: ReadonlySet<AvailablePermissions> = new Set([
  'configurate_action_settings',
  'create_action',
  'delete_action',
  'edit_action',
]);

const EDIT_PERMISSIONS: ReadonlySet<AvailablePermissions> = new Set([
  ...ACTION_PERMISSIONS,
  'add_canvas_action',
  'add_custom_intent',
  'add_step',
  'add_suggested_improvements',
  'add_workflow_tag',
  'customize_action_settings',
  'delete_canvas_action',
  'delete_step',
  'discard_draft_workflow',
  'edit_step',
  'preview_workflows',
  'remove_workflow_tag',
  'route_intent',
  'squeeze_step',
  'update_workflow',
]);

const PUBLISH_PERMISSIONS: ReadonlySet<AvailablePermissions> = new Set([
  'activate_workflow',
  'delete_custom_intent',
]);

export const channelForProduct = (
  product: SolveWidgetProduct | string | null | undefined,
): SolveChannel | undefined =>
  product ? PRODUCT_TO_CHANNEL[product as SolveWidgetProduct] : undefined;

export const meetsLevel = (
  level: ChannelAccessLevel | undefined,
  required: ChannelAccessLevel,
): boolean => LEVEL_RANK[level ?? 'none'] >= LEVEL_RANK[required];

export const highestLevel = (
  levels: Partial<Record<SolveChannel, ChannelAccessLevel>>,
): ChannelAccessLevel =>
  Object.values(levels).reduce<ChannelAccessLevel>(
    (highest, level) => (meetsLevel(level, highest) ? level : highest),
    'none',
  );

export const levelAllows = (
  level: ChannelAccessLevel | undefined,
  permission: AvailablePermissions,
): boolean => {
  if (PUBLISH_PERMISSIONS.has(permission)) {
    return meetsLevel(level, 'publish');
  }
  if (EDIT_PERMISSIONS.has(permission)) {
    return meetsLevel(level, 'edit');
  }
  return false;
};

// Not FLAMETHROWER itself: it also gates role-based pages such as Action Builder settings.
const GRANTABLE_PERMISSION_KEYS = [
  Permission.FLAMETHROWER_WORKFLOW_BUILDER_EDIT,
  Permission.ACTION_BUILDER,
  Permission.WORKFLOW_BUILDER,
  Permission.SOLVE_PREVIEW,
  Permission.SOLVE_INSIGHTS_VIEW,
];

/** Role-based values for the keys channel grants can open; all mirror FLAMETHROWER. */
export const withAgentBuilderAliases = <
  T extends Record<string, boolean | undefined>,
>(
  permissions: T,
): T => ({
  ...permissions,
  [Permission.ACTION_BUILDER]: permissions[Permission.FLAMETHROWER],
  [Permission.FLAMETHROWER_WORKFLOW_BUILDER_EDIT]:
    permissions[Permission.FLAMETHROWER],
  [Permission.SOLVE_INSIGHTS_VIEW]: permissions[Permission.FLAMETHROWER],
});

export const applyChannelGrantsToPermissions = <
  T extends Record<string, boolean | undefined>,
>(
  permissions: T,
  channelAccess: CurrentUserChannelAccess | undefined,
): T => {
  if (!channelAccess?.has_grants) {
    return permissions;
  }
  const canViewAnyChannel = Object.values(channelAccess.levels).some(level =>
    meetsLevel(level, 'view'),
  );
  return {
    ...permissions,
    ...Object.fromEntries(
      GRANTABLE_PERMISSION_KEYS.map(key => [key, canViewAnyChannel]),
    ),
    // A granted user with no view anywhere still reaches the Solve tab so it can
    // show the no-access state; the pages' channel guards render it and the data
    // routes stay closed server-side.
    [Permission.SOLVE_INSIGHTS_VIEW]: true,
  };
};

export const TAB_CHANNEL_TO_SOLVE_CHANNEL: Record<
  string,
  SolveChannel | undefined
> = {
  api: 'headless',
  email: 'email',
  slack: 'slack',
  voice: 'voice',
  web_call: 'voice',
  widget: 'widget',
};

/** Conversation `channel` values that don't name a grantable channel are widget-family. Mirrors INSIGHTS_VALUE_TO_CHANNEL on the server. */
export const conversationChannelToSolveChannel = (
  channel: string | null | undefined,
): SolveChannel =>
  TAB_CHANNEL_TO_SOLVE_CHANNEL[channel ?? 'widget'] ?? 'widget';

/** Channel tab params (`widget`, `api`, `web_call`, ...) the user can't view under their grants. */
export const isChannelTabRestricted = (
  tabChannel: string | null | undefined,
  hasGrants: boolean,
  visibleChannels: ReadonlySet<SolveChannel>,
): boolean => {
  if (!hasGrants || !tabChannel) {
    return false;
  }
  const channel = TAB_CHANNEL_TO_SOLVE_CHANNEL[tabChannel];
  return !channel || !visibleChannels.has(channel);
};

/** Translation drawer tabs; `email` is the legacy email builder, not the grantable Email channel. */
export const TRANSLATION_CHANNEL_TO_SOLVE_CHANNEL: Record<
  string,
  SolveChannel | undefined
> = {
  api: 'headless',
  interactive_email: 'email',
  slack: 'slack',
  voice: 'voice',
  web_call: 'voice',
  widget: 'widget',
};
